import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Plus,
  Clock,
  MessageSquare,
  AlertCircle,
  X,
  Trash2,
  AlignLeft,
  CircleCheck,
  Filter,
  MoreHorizontal,
  Lock,
  Globe,
  Minimize2,
  Users,
  UserPlus,
  Paperclip,
  Check,
  Archive,
  Columns3,
  RotateCcw,
  Pencil,
} from 'lucide-react'
import { Button, ConfirmDialog, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import {
  useBoard,
  useUpdateBoard,
  useBoardLists,
  useBoardCards,
  useBoardMembers,
  useAddBoardMember,
  useRemoveBoardMember,
  useCreateList,
  useDeleteList,
  useUpdateList,
  useReorderLists,
  useArchivedBoardCards,
  useCreateCard,
  useMoveCard,
  useUpdateCard,
  useBoardLabels,
  useUpdateLabel,
  useDeleteLabel,
} from '../hooks/useBoards'
import { useUsersList } from '@/features/users/hooks/useUsers'
import { CardDetailModal } from './CardDetailModal'
import { BOARD_BACKGROUND_PRESETS, BOARD_SOLID_BACKGROUNDS } from './boardBackgrounds'
import { cn } from '@/lib/cn'
import { getAvatarColorClass, getAvatarColorClassByIndex } from '@/components/common/Avatar'
import { useFullscreenShortcut } from '@/hooks/useFullscreenShortcut'
import type { BoardMemberData, CardData } from '@/api/services/boards.service'

const PRIORITY_CONFIG: Record<string, { color: string; label: string; accent: string }> = {
  urgent: { color: 'bg-red-500', label: 'Urgent', accent: '#ef4444' },
  high: { color: 'bg-orange-500', label: 'High', accent: '#f97316' },
  medium: { color: 'bg-yellow-500', label: 'Medium', accent: '#ca8a04' },
  low: { color: 'bg-blue-500', label: 'Low', accent: '#3b82f6' },
  none: { color: '', label: '', accent: '' },
}

const CARD_RELATION_OPTIONS = [
  { value: 'time-correction', label: 'Time corrections' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'leave', label: 'Leave requests' },
  { value: 'shift', label: 'Shifts' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'penalty', label: 'Penalties' },
  { value: 'bonus', label: 'Bonuses' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other work' },
] as const

function getCardRelationLabel(entityType?: string): string | null {
  if (!entityType) return null
  const normalized = entityType.trim().toLowerCase().replace(/[_\s]/g, '-')
  const relation = CARD_RELATION_OPTIONS.find((option) => option.value === normalized)
  if (relation) return relation.label
  if (normalized === 'timecorrection') return 'Time corrections'
  return entityType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

/** ClickUp-style status colors for list headers */
const LIST_STATUS_COLORS = [
  '#7b68ee', '#49ccf9', '#ff6900', '#00c875', '#e2445c',
  '#fdab3d', '#0086c0', '#9d99b9', '#579bfc', '#cab641',
]

function getListStatusColor(index: number): string {
  return LIST_STATUS_COLORS[index % LIST_STATUS_COLORS.length]
}

type FilterMode = 'all' | 'priority' | 'assignee' | 'label' | 'dueDate' | 'status'
type DropTarget = { listId: string; index: number }
type ArchiveView = 'cards' | 'columns'

const DRAG_START_DISTANCE = 5

export function BoardViewPage(): React.ReactElement {
  const { boardId } = useParams<{ boardId: string }>()
  const navigate = useNavigate()
  const { data: board, isLoading: boardLoading } = useBoard(boardId ?? null)
  const { data: lists, isLoading: listsLoading } = useBoardLists(boardId ?? null)
  const { data: cards, isLoading: cardsLoading } = useBoardCards(boardId ?? null)
  const { data: members } = useBoardMembers(boardId ?? null)
  const addBoardMemberMutation = useAddBoardMember()
  const removeBoardMemberMutation = useRemoveBoardMember()
  const createList = useCreateList()
  const deleteList = useDeleteList()
  const updateList = useUpdateList()
  const reorderLists = useReorderLists()
  const createCard = useCreateCard()
  const moveCardMutation = useMoveCard()
  const updateCardMutation = useUpdateCard()
  const updateBoardMutation = useUpdateBoard()
  const { data: boardLabels } = useBoardLabels(boardId ?? null)
  const updateLabelMutation = useUpdateLabel()
  const deleteLabelMutation = useDeleteLabel()
  const canEditBoard = ['owner', 'admin', 'member'].includes(board?.currentUserRole ?? '')
  const canManageBoard = ['owner', 'admin'].includes(board?.currentUserRole ?? '')

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [addingCardToList, setAddingCardToList] = useState<string | null>(null)
  const [newCardTitle, setNewCardTitle] = useState('')
  const [addingList, setAddingList] = useState(false)
  const [newListTitle, setNewListTitle] = useState('')
  const [listPendingArchive, setListPendingArchive] = useState<{ id: string; title: string } | null>(null)
  const [editingListId, setEditingListId] = useState<string | null>(null)
  const [listTitleDraft, setListTitleDraft] = useState('')
  const skipListBlurRef = useRef(false)
  const [draggedListId, setDraggedListId] = useState<string | null>(null)
  const [listDropTargetId, setListDropTargetId] = useState<string | null>(null)
  const [showArchivedColumns, setShowArchivedColumns] = useState(false)
  const [archiveView, setArchiveView] = useState<ArchiveView>('cards')
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false)
  const [showRenameBoard, setShowRenameBoard] = useState(false)
  const [boardTitleDraft, setBoardTitleDraft] = useState('')
  const [editingBoardTitle, setEditingBoardTitle] = useState(false)
  const [boardDescriptionDraft, setBoardDescriptionDraft] = useState('')
  const [editingBoardDescription, setEditingBoardDescription] = useState(false)
  const [showMemberPicker, setShowMemberPicker] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<BoardMemberData | null>(null)
  const memberPickerRef = useRef<HTMLDivElement>(null)
  const { data: boardUsersData } = useUsersList({ page: 1, limit: 100 }, { enabled: showMemberPicker })
  const { data: archivedLists, isLoading: archivedListsLoading } = useBoardLists(boardId ?? null, true, showArchivedColumns)
  const { data: archivedCards, isLoading: archivedCardsLoading } = useArchivedBoardCards(boardId ?? null, showArchivedColumns)
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const grabOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const [dragPreviewWidth, setDragPreviewWidth] = useState(260)
  const [dragPreviewHeight, setDragPreviewHeight] = useState(72)
  const dragPointerRef = useRef({ x: 0, y: 0 })
  const dragFrameRef = useRef<number | null>(null)
  const activeDragIdRef = useRef<string | null>(null)
  const pendingDragRef = useRef<{
    cardId: string
    pointerId: number
    startX: number
    startY: number
    offsetX: number
    offsetY: number
    width: number
    height: number
  } | null>(null)
  const suppressCardClickRef = useRef(false)
  const [isBoardFullscreen, setIsBoardFullscreen] = useState(false)

  useEffect(() => {
    if (!addingCardToList) return undefined
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-add-card-form="${addingCardToList}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [addingCardToList])

  const saveListTitle = useCallback((listId: string) => {
    const title = listTitleDraft.trim()
    const current = lists?.find((list) => list._id === listId)
    if (title && current && title !== current.title) {
      updateList.mutate({ boardId: boardId!, listId, payload: { title } })
    }
    setEditingListId(null)
  }, [boardId, listTitleDraft, lists, updateList])

  const handleListDrop = useCallback((targetListId: string) => {
    if (!draggedListId || !lists || draggedListId === targetListId || !boardId) return
    const ids = lists.map((list) => list._id)
    const fromIndex = ids.indexOf(draggedListId)
    const targetIndex = ids.indexOf(targetListId)
    if (fromIndex < 0 || targetIndex < 0) return
    ids.splice(fromIndex, 1)
    ids.splice(fromIndex < targetIndex ? targetIndex - 1 : targetIndex, 0, draggedListId)
    reorderLists.mutate({ boardId, listIds: ids })
    setDraggedListId(null)
    setListDropTargetId(null)
  }, [boardId, draggedListId, lists, reorderLists])

  useFullscreenShortcut({
    isFullscreen: isBoardFullscreen,
    setIsFullscreen: setIsBoardFullscreen,
    canEnter: Boolean(board),
  })

  const draggedCard = useMemo(() => cards?.find((c) => c._id === draggedCardId), [cards, draggedCardId])

  // Move the preview outside React's render cycle, once per animation frame.
  useEffect(() => {
    if (!draggedCardId) {
      if (overlayRef.current) overlayRef.current.style.display = 'none'
      return
    }
    if (overlayRef.current) {
      overlayRef.current.style.display = 'block'
      overlayRef.current.style.transform = `translate3d(${dragPointerRef.current.x - grabOffsetRef.current.x}px, ${dragPointerRef.current.y - grabOffsetRef.current.y}px, 0) rotate(3.5deg) scale(1.03)`
    }
    return () => {
      if (dragFrameRef.current !== null) {
        cancelAnimationFrame(dragFrameRef.current)
        dragFrameRef.current = null
      }
    }
  }, [draggedCardId])

  // Positional drop target: which list and at which index the card will be inserted
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)
  // Ref to track last drop target and avoid redundant setState calls
  const dropTargetRef = useRef<DropTarget | null>(null)

  /** Update drop target only when it actually changes (prevents redundant re-renders) */
  const updateDropTarget = useCallback((target: DropTarget | null) => {
    const prev = dropTargetRef.current
    if (prev?.listId === target?.listId && prev?.index === target?.index) return
    dropTargetRef.current = target
    setDropTarget(target)
  }, [])

  // Filter state
  const [filterOpen, setFilterOpen] = useState(false)
  const [activeFilter, setActiveFilter] = useState<FilterMode>('all')
  const [filterValue, setFilterValue] = useState('')
  const filterRef = useRef<HTMLDivElement>(null)
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null)
  const [labelDraft, setLabelDraft] = useState('')
  const skipLabelBlurRef = useRef(false)
  const [labelPendingDelete, setLabelPendingDelete] = useState<{ id: string; name: string } | null>(null)

  // More menu state
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const moreMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false)
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false)
      }
      if (memberPickerRef.current && !memberPickerRef.current.contains(e.target as Node)) {
        setShowMemberPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const cardsByList = useMemo(() => {
    const map: Record<string, CardData[]> = {}
    if (lists) {
      for (const list of lists) {
        map[list._id] = []
      }
    }
    if (cards) {
      for (const card of cards) {
        if (!map[card.listId]) map[card.listId] = []
        map[card.listId].push(card)
      }
    }
    // Sort cards by position within each list
    for (const listId of Object.keys(map)) {
      map[listId].sort((a, b) => a.position - b.position)
    }
    return map
  }, [lists, cards])

  // Apply filters to cards
  const filteredCardsByList = useMemo(() => {
    if (activeFilter === 'all' || !filterValue) return cardsByList
    const filtered: Record<string, CardData[]> = {}
    for (const [listId, listCards] of Object.entries(cardsByList)) {
      filtered[listId] = listCards.filter((card) => {
        switch (activeFilter) {
          case 'priority':
            return card.priority === filterValue
          case 'assignee':
            return card.assignees?.some((a) => {
              const isObj = typeof a === 'object' && a !== null
              return isObj ? a._id === filterValue : false
            })
          case 'label':
            return card.labels?.some((l) => l._id === filterValue)
          case 'dueDate':
            if (filterValue === 'overdue') {
              return card.dueDate && new Date(card.dueDate) < new Date() && !card.completedAt
            }
            if (filterValue === 'today') {
              if (!card.dueDate) return false
              const due = new Date(card.dueDate)
              const now = new Date()
              return due.toDateString() === now.toDateString()
            }
            if (filterValue === 'week') {
              if (!card.dueDate) return false
              const due = new Date(card.dueDate)
              const now = new Date()
              const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
              return due >= now && due <= weekFromNow
            }
            return true
          case 'status':
            return filterValue === 'complete' ? Boolean(card.completedAt) : !card.completedAt
          default:
            return true
        }
      })
    }
    return filtered
  }, [cardsByList, activeFilter, filterValue])

  const handleAddCard = useCallback((listId: string) => {
    if (!newCardTitle.trim() || !boardId) return
    createCard.mutate({
      boardId,
      payload: {
        title: newCardTitle.trim(),
        listId,
      },
    })
    setNewCardTitle('')
    setAddingCardToList(null)
  }, [newCardTitle, boardId, createCard])

  const handleAddList = useCallback(() => {
    if (!newListTitle.trim() || !boardId) return
    createList.mutate({
      boardId,
      payload: { title: newListTitle.trim() },
    })
    setNewListTitle('')
    setAddingList(false)
  }, [newListTitle, boardId, createList])

  const handleCardCompletion = useCallback((event: React.MouseEvent<HTMLButtonElement>, card: CardData) => {
    event.preventDefault()
    event.stopPropagation()
    if (!boardId) return
    updateCardMutation.mutate({
      boardId,
      cardId: card._id,
      payload: { completedAt: card.completedAt ? null : new Date().toISOString() },
    })
  }, [boardId, updateCardMutation])

  // ─── Smooth pointer-driven drag & drop ───
  const positionDragPreview = useCallback((clientX: number, clientY: number) => {
    dragPointerRef.current = { x: clientX, y: clientY }
    if (dragFrameRef.current !== null) return
    dragFrameRef.current = requestAnimationFrame(() => {
      dragFrameRef.current = null
      if (overlayRef.current) {
        overlayRef.current.style.transform = `translate3d(${dragPointerRef.current.x - grabOffsetRef.current.x}px, ${dragPointerRef.current.y - grabOffsetRef.current.y}px, 0) rotate(3.5deg) scale(1.03)`
      }
    })
  }, [])

  const getDropTargetAtPoint = useCallback((clientX: number, clientY: number): DropTarget | null => {
    const element = document.elementFromPoint(clientX, clientY) as HTMLElement | null
    const placeholder = element?.closest<HTMLElement>('[data-board-drop-placeholder]')
    if (placeholder?.dataset.listId && placeholder.dataset.cardIndex) {
      return { listId: placeholder.dataset.listId, index: Number(placeholder.dataset.cardIndex) }
    }
    const cardElement = element?.closest<HTMLElement>('[data-board-card]')
    if (cardElement) {
      const listId = cardElement.dataset.listId
      const index = Number(cardElement.dataset.cardIndex)
      if (!listId || Number.isNaN(index)) return null
      const rect = cardElement.getBoundingClientRect()
      return { listId, index: clientY < rect.top + rect.height / 2 ? index : index + 1 }
    }

    const column = element?.closest<HTMLElement>('[data-board-list-id]')
    if (!column?.dataset.boardListId) return null
    if (element?.closest('.board-column-header')) return { listId: column.dataset.boardListId, index: 0 }
    if (element?.closest('.board-column-cards')) {
      return { listId: column.dataset.boardListId, index: Number(column.dataset.cardCount ?? 0) }
    }
    return null
  }, [])

  const handleCardPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, cardId: string) => {
    if (e.button !== 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    e.currentTarget.setPointerCapture(e.pointerId)
    pendingDragRef.current = {
      cardId,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    }
  }, [])

  /** Calculate the position value for inserting at `index` in a sorted card list */
  const calcPosition = useCallback((sortedCards: CardData[], index: number, draggedId: string): number => {
    // Filter out the dragged card from the list (for same-list reorder)
    const filtered = sortedCards.filter((c) => c._id !== draggedId)
    if (filtered.length === 0) return 1000
    if (index <= 0) return (filtered[0]?.position ?? 1000) / 2
    if (index >= filtered.length) return (filtered[filtered.length - 1]?.position ?? 0) + 1000
    const before = filtered[index - 1]?.position ?? 0
    const after = filtered[index]?.position ?? before + 2000
    return (before + after) / 2
  }, [])

  const finishDrag = useCallback(() => {
    const cardId = activeDragIdRef.current
    const target = dropTargetRef.current
    activeDragIdRef.current = null
    if (overlayRef.current) overlayRef.current.style.display = 'none'
    setDraggedCardId(null)
    updateDropTarget(null)
    if (!cardId || !boardId || !target) return

    const sortedCards = filteredCardsByList[target.listId] ?? []
    const draggedCard = cards?.find((c) => c._id === cardId)

    // Skip if dropping at same position in same list
    if (draggedCard?.listId === target.listId) {
      const currentIndex = sortedCards.findIndex((c) => c._id === cardId)
      if (currentIndex === target.index || currentIndex === target.index - 1) return
    }

    const position = calcPosition(sortedCards, target.index, cardId)

    moveCardMutation.mutate({
      boardId,
      cardId,
      payload: { listId: target.listId, position },
    })
  }, [boardId, cards, filteredCardsByList, calcPosition, moveCardMutation, updateDropTarget])

  const handleCardPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pendingDrag = pendingDragRef.current
    if (!pendingDrag || pendingDrag.pointerId !== e.pointerId) return

    if (!activeDragIdRef.current) {
      const distance = Math.hypot(e.clientX - pendingDrag.startX, e.clientY - pendingDrag.startY)
      if (distance < DRAG_START_DISTANCE) return
      grabOffsetRef.current = { x: pendingDrag.offsetX, y: pendingDrag.offsetY }
      setDragPreviewWidth(pendingDrag.width)
      setDragPreviewHeight(pendingDrag.height)
      dragPointerRef.current = { x: e.clientX, y: e.clientY }
      activeDragIdRef.current = pendingDrag.cardId
      suppressCardClickRef.current = true
      setDraggedCardId(pendingDrag.cardId)
    }

    positionDragPreview(e.clientX, e.clientY)
    updateDropTarget(getDropTargetAtPoint(e.clientX, e.clientY))
  }, [getDropTargetAtPoint, positionDragPreview, updateDropTarget])

  const handleCardPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (pendingDragRef.current?.pointerId !== e.pointerId) return
    const wasDragging = Boolean(activeDragIdRef.current)
    pendingDragRef.current = null
    if (wasDragging) finishDrag()
  }, [finishDrag])

  const handleCardPointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (pendingDragRef.current?.pointerId !== e.pointerId) return
    pendingDragRef.current = null
    activeDragIdRef.current = null
    if (overlayRef.current) overlayRef.current.style.display = 'none'
    setDraggedCardId(null)
    updateDropTarget(null)
  }, [updateDropTarget])

  const handleCardClick = useCallback((cardId: string) => {
    if (suppressCardClickRef.current) {
      suppressCardClickRef.current = false
      return
    }
    setSelectedCardId(cardId)
  }, [])

  const handleClearFilter = useCallback(() => {
    setActiveFilter('all')
    setFilterValue('')
    setFilterOpen(false)
  }, [])

  const handleToggleVisibility = useCallback(async () => {
    if (!boardId || !board) return
    const newVisibility = board.visibility === 'private' ? 'workspace' : 'private'
    await updateBoardMutation.mutateAsync({
      id: boardId,
      payload: { visibility: newVisibility },
    })
    setMoreMenuOpen(false)
  }, [boardId, board, updateBoardMutation])

  const saveInlineBoardTitle = useCallback(() => {
    if (!boardId || !boardTitleDraft.trim() || boardTitleDraft.trim() === board?.title) {
      setEditingBoardTitle(false)
      return
    }
    updateBoardMutation.mutate({ id: boardId, payload: { title: boardTitleDraft.trim() }, silent: true })
    setEditingBoardTitle(false)
  }, [boardId, board?.title, boardTitleDraft, updateBoardMutation])

  const saveInlineBoardDescription = useCallback(() => {
    if (!boardId || !board) {
      setEditingBoardDescription(false)
      return
    }
    const description = boardDescriptionDraft.trim()
    if (description !== (board.description ?? '').trim()) {
      updateBoardMutation.mutate({ id: boardId, payload: { description }, silent: true })
    }
    setEditingBoardDescription(false)
  }, [board, boardDescriptionDraft, boardId, updateBoardMutation])

  const allLabels = useMemo(() => {
    const map = new Map<string, { _id: string; name: string; color: string }>()
    if (cards) cards.forEach((c) => c.labels?.forEach((l) => map.set(l._id, l)))
    return Array.from(map.values())
  }, [cards])

  const availableLabels = boardLabels ?? allLabels

  const saveLabel = useCallback((labelId: string) => {
    const name = labelDraft.trim()
    const current = availableLabels.find((label) => label._id === labelId)
    if (boardId && name && current && name !== current.name) {
      updateLabelMutation.mutate({ boardId, labelId, name, color: current.color })
    }
    setEditingLabelId(null)
  }, [availableLabels, boardId, labelDraft, updateLabelMutation])

  const allAssignees = useMemo(() => {
    const map = new Map<string, { _id: string; firstName: string; lastName: string }>()
    if (cards) {
      cards.forEach((c) =>
        c.assignees?.forEach((a) => {
          if (typeof a === 'object' && a !== null) map.set(a._id, a)
        })
      )
    }
    return Array.from(map.values())
  }, [cards])

  // Board members for avatar display
  const membersList = useMemo(() => {
    if (!members || !Array.isArray(members)) return []
    return members as BoardMemberData[]
  }, [members])

  const availableBoardUsers = useMemo(() => {
    const existingIds = new Set(membersList.map((member) => member.userId._id))
    const search = memberSearch.trim().toLowerCase()
    return (boardUsersData?.data ?? []).filter((user) => {
      if (existingIds.has(user._id)) return false
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''} ${user.email ?? ''}`.toLowerCase()
      return !search || name.includes(search)
    })
  }, [boardUsersData?.data, memberSearch, membersList])

  const isLoading = boardLoading || listsLoading || cardsLoading

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-[#f0f1f3] p-6 dark:bg-[#1e1f21]">
        <SectionLoader variant="cards" label="Loading board" />
      </div>
    )
  }

  if (!board) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center gap-4 bg-[#f0f1f3] dark:bg-[#1e1f21]">
        <AlertCircle className="h-12 w-12 text-gray-400" />
        <p className="text-gray-500 dark:text-gray-400">Board not found</p>
        <Button variant="secondary" onClick={() => navigate('/boards')}>
          Back to Boards
        </Button>
      </div>
    )
  }

  const isFilterActive = activeFilter !== 'all' && filterValue !== ''
  const normalizedBoardBackground = board.background?.trim().toLowerCase()
  const isDefaultBoardBackground = !normalizedBoardBackground || normalizedBoardBackground === '#ffffff' || normalizedBoardBackground === '#fff'
  const hasCustomBoardBackground = !isDefaultBoardBackground
  const useLightHeaderText = hasCustomBoardBackground

  return (
    <div className={cn(
      'board-fullscreen relative flex flex-col bg-[#f0f1f3] dark:bg-[#1e1f21]',
      isBoardFullscreen ? 'fixed inset-0 z-50 h-screen' : 'h-[calc(100vh-4rem)]',
    )}>
      {/* Keep primary board controls available in both normal and fullscreen views. */}
      <div
        className={cn(
          'board-toolbar z-20 shrink-0 border-b px-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] backdrop-blur md:px-5',
          hasCustomBoardBackground
            ? 'border-white/20'
            : 'border-gray-200 bg-white/95 dark:border-[#343537] dark:bg-[#282a2c]/95',
        )}
        style={hasCustomBoardBackground ? { background: board.background } : undefined}
      >
        <div className="flex h-12 items-center justify-between gap-3">
          {/* Left: Back + Board info */}
          <div className="flex items-center gap-2 min-w-0">
            {isBoardFullscreen && (
              <>
                <button
                  type="button"
                  onClick={() => setIsBoardFullscreen(false)}
              className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all', useLightHeaderText ? 'text-white/90 hover:bg-white/15 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/8 dark:hover:text-gray-200')}
                  title="Exit full screen (press F or Esc)"
                >
                  <Minimize2 className="h-4 w-4" />
                </button>
                <div className={cn('h-4 w-px', useLightHeaderText ? 'bg-white/30' : 'bg-gray-200 dark:bg-gray-700')} />
              </>
            )}

            <div className="flex items-center gap-2 min-w-0">
              {editingBoardTitle ? (
                <input
                  autoFocus
                  value={boardTitleDraft}
                  onChange={(event) => setBoardTitleDraft(event.target.value)}
                  onBlur={saveInlineBoardTitle}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') saveInlineBoardTitle()
                    if (event.key === 'Escape') {
                      setBoardTitleDraft(board.title)
                      setEditingBoardTitle(false)
                    }
                  }}
                  className={cn(
                    'h-7 max-w-[min(28rem,48vw)] rounded-md border px-1.5 text-[15px] font-semibold tracking-tight outline-none ring-2',
                    useLightHeaderText
                      ? 'border-white/60 bg-white/10 text-white caret-white ring-white/20'
                      : 'border-primary/40 bg-white text-gray-900 ring-primary/15 dark:bg-[#343537] dark:text-gray-100',
                  )}
                  style={{ width: `${Math.max(boardTitleDraft.length + 1, 4)}ch` }}
                  aria-label="Board name"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!canEditBoard) return
                    setBoardTitleDraft(board.title)
                    setEditingBoardTitle(true)
                  }}
                  className={cn(
                    'truncate text-left text-[15px] font-semibold tracking-tight',
                    useLightHeaderText ? 'text-white' : 'text-gray-900 dark:text-gray-100',
                    canEditBoard ? cn('cursor-pointer rounded px-1 -mx-1 transition-colors', useLightHeaderText ? 'hover:bg-white/15' : 'hover:bg-gray-100 dark:hover:bg-white/8') : 'cursor-default',
                  )}
                  title={canEditBoard ? 'Click to rename board' : undefined}
                >
                  {board.title}
                </button>
              )}
              <span
                className={cn(
                  'inline-flex h-6 shrink-0 cursor-default items-center gap-1 rounded-full border px-2 text-[10px] font-semibold',
                  useLightHeaderText ? 'border-white/25 bg-white/5 text-white/85' : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-gray-400',
                )}
                title={`Visibility: ${board.visibility === 'private' ? 'Private — Only board members' : 'Workspace — All team members'}`}
              >
                {board.visibility === 'private' ? (
                  <><Lock className="h-3 w-3" /><span>Private</span></>
                ) : (
                  <><Globe className="h-3 w-3" /><span>Workspace</span></>
                )}
              </span>
            </div>

            {(board.description?.trim() || canEditBoard) && (
              <>
                <div className={cn('hidden h-4 w-px sm:block', useLightHeaderText ? 'bg-white/30' : 'bg-gray-200 dark:bg-gray-700')} />
                {editingBoardDescription ? (
                  <input
                    autoFocus
                    value={boardDescriptionDraft}
                    onChange={(event) => setBoardDescriptionDraft(event.target.value)}
                    onBlur={saveInlineBoardDescription}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') saveInlineBoardDescription()
                      if (event.key === 'Escape') {
                        setBoardDescriptionDraft(board.description ?? '')
                        setEditingBoardDescription(false)
                      }
                    }}
                    className={cn(
                      'hidden h-7 min-w-32 max-w-[min(26vw,240px)] rounded-md border px-1.5 text-xs outline-none ring-2 sm:block',
                      useLightHeaderText
                        ? 'border-white/60 bg-white/10 text-white caret-white ring-white/20'
                        : 'border-primary/40 bg-white text-gray-700 ring-primary/15 dark:bg-[#343537] dark:text-gray-200',
                    )}
                    style={{ width: `${Math.max(Math.min(boardDescriptionDraft.length + 1, 28), 16)}ch` }}
                    aria-label="Board description"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEditBoard) return
                      setBoardDescriptionDraft(board.description ?? '')
                      setEditingBoardDescription(true)
                    }}
                    className={cn(
                      'hidden max-w-[min(26vw,240px)] truncate rounded px-1 -mx-1 text-left text-xs transition-colors sm:block',
                      useLightHeaderText ? 'text-white/75' : 'text-gray-400 dark:text-gray-500',
                      canEditBoard ? cn('cursor-pointer', useLightHeaderText ? 'hover:bg-white/15 hover:text-white' : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/8 dark:hover:text-gray-300') : 'cursor-default',
                    )}
                    title={canEditBoard ? 'Click to edit board description' : board.description}
                  >
                    {board.description?.trim() || 'Add description'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Board members avatars — real data */}
            <div className="relative mr-1 flex items-center gap-1.5" ref={memberPickerRef}>
              <div className="flex -space-x-1.5">
                {membersList.slice(0, 3).map((m, index) => {
                  const user = m.userId
                  const initials = `${(user.firstName?.[0] || '')}${(user.lastName?.[0] || '')}`.toUpperCase() || '?'
                  return (
                    <div
                      key={m._id}
                      className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white shadow-sm', getAvatarColorClassByIndex(index))}
                      title={`${user.firstName} ${user.lastName} (${m.role})`}
                    >
                      {initials}
                    </div>
                  )
                })}
                {membersList.length > 3 && (
                  <div
                    className={cn('flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-[9px] font-bold shadow-sm', useLightHeaderText ? 'bg-white/20 text-white' : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200')}
                    title={`${membersList.length - 3} more member${membersList.length - 3 === 1 ? '' : 's'}`}
                  >
                    +{membersList.length - 3}
                  </div>
                )}
              </div>
              {membersList.length === 0 && (
                <div className={cn('flex items-center gap-1 text-xs', useLightHeaderText ? 'text-white/75' : 'text-gray-400 dark:text-gray-500')}>
                  <Users className="h-3.5 w-3.5" />
                </div>
              )}
              {canManageBoard && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMemberPicker((open) => !open)
                    setMemberSearch('')
                  }}
                  className={cn(
                    'ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full p-0 transition-all duration-150',
                    useLightHeaderText
                      ? 'text-white hover:bg-white/20'
                      : 'text-slate-600 hover:bg-slate-200/80 dark:text-gray-200 dark:hover:bg-white/10',
                  )}
                  title="Add board member"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                </button>
              )}

              {showMemberPicker && canManageBoard && (
                <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-300 bg-white p-2 shadow-[0_12px_32px_rgba(15,23,42,0.16)] dark:border-[#3a3b3d] dark:bg-[#282a2c]">
                  <div className="px-1.5 pb-2 pt-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">Add members</p>
                    <input
                      autoFocus
                      value={memberSearch}
                      onChange={(event) => setMemberSearch(event.target.value)}
                      placeholder="Search workspace members"
                      className="mt-2 h-8 w-full rounded-lg border border-slate-300 bg-white px-2.5 text-xs text-gray-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-[#343537] dark:text-gray-100"
                    />
                  </div>
                  {membersList.length > 0 && (
                    <div className="mb-2 border-y border-slate-200 px-1 py-2 dark:border-white/10">
                      <p className="px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">Board members</p>
                      <div className="max-h-28 space-y-0.5 overflow-y-auto thin-scroll">
                        {membersList.map((member, index) => {
                          const memberUser = member.userId
                          const canRemove = member.role !== 'owner'
                          return (
                            <div key={member._id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                              <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white', getAvatarColorClassByIndex(index))}>
                                {memberUser.firstName?.[0]}{memberUser.lastName?.[0]}
                              </div>
                              <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                                {memberUser.firstName} {memberUser.lastName}
                              </span>
                              <span className="text-[10px] capitalize text-gray-400 dark:text-gray-500">{member.role}</span>
                              {canRemove && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMemberPendingRemoval(member)
                                  }}
                                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                                  title={`Remove ${memberUser.firstName} ${memberUser.lastName}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  <div className="max-h-52 space-y-0.5 overflow-y-auto px-1 thin-scroll">
                    {availableBoardUsers.length > 0 ? availableBoardUsers.map((user) => (
                      <button
                        key={user._id}
                        type="button"
                        onClick={() => {
                          if (!boardId) return
                          addBoardMemberMutation.mutate({ boardId, user })
                          setShowMemberPicker(false)
                        }}
                        className="flex w-full items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-left transition-colors hover:border-slate-200 hover:bg-slate-50 dark:hover:border-white/10 dark:hover:bg-white/5"
                      >
                        <div className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white', getAvatarColorClass(user._id))}>
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </div>
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                          {user.firstName} {user.lastName}
                        </span>
                        <Plus className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    )) : (
                      <p className="px-2 py-4 text-center text-xs text-gray-500 dark:text-gray-400">No available members found</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={cn('mx-1 hidden h-5 w-px sm:block', useLightHeaderText ? 'bg-white/30' : 'bg-gray-200 dark:bg-gray-700')} />

            {/* ─── Filter Button ─── */}
            <div className="relative hidden sm:block" ref={filterRef}>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all',
                    useLightHeaderText
                      ? (isFilterActive ? 'bg-white/25 text-white' : 'text-white/90 hover:bg-white/15')
                      : (isFilterActive ? 'bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/8')
                  )}
                >
                  <Filter className="h-3.5 w-3.5" />
                  <span>Filter</span>
                </button>
                {isFilterActive && (
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    className={cn('ml-0.5 rounded-full p-0.5', useLightHeaderText ? 'text-white hover:bg-white/20' : 'text-primary hover:bg-primary/20 dark:text-primary-light dark:hover:bg-primary-light/30')}
                    title="Clear filter"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>

              {/* Filter Dropdown */}
              {filterOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-300 bg-white p-2 shadow-[0_12px_32px_rgba(15,23,42,0.16)] dark:border-[#3a3b3d] dark:bg-[#282a2c]">
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">Filter by</span>
                    {isFilterActive && (
                      <button
                        type="button"
                        onClick={handleClearFilter}
                        className="rounded-md px-1.5 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/10"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Priority filter */}
                  <div className="rounded-lg border border-slate-300 bg-gray-50 p-2 dark:border-white/10 dark:bg-white/[0.035]">
                    <p className="px-1 pb-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300">Priority</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => {
                            setActiveFilter('priority')
                            setFilterValue(p)
                            setFilterOpen(false)
                          }}
                          className={cn(
                            'flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                            activeFilter === 'priority' && filterValue === p
                              ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20'
                              : 'border-slate-300 bg-white text-gray-600 hover:border-slate-400 hover:bg-gray-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400 dark:hover:border-white/10 dark:hover:bg-white/8'
                          )}
                        >
                          <span className={cn('h-1.5 w-1.5 rounded-full', PRIORITY_CONFIG[p]?.color)} />
                          {PRIORITY_CONFIG[p]?.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Due Date filter */}
                  <div className="mt-2 rounded-lg border border-slate-300 bg-gray-50 p-2 dark:border-white/10 dark:bg-white/[0.035]">
                    <p className="px-1 pb-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300">Due date</p>
                    <div className="grid grid-cols-3 gap-1">
                      {[
                        { key: 'overdue', label: 'Overdue' },
                        { key: 'today', label: 'Due Today' },
                        { key: 'week', label: 'Due This Week' },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => {
                            setActiveFilter('dueDate')
                            setFilterValue(opt.key)
                            setFilterOpen(false)
                          }}
                          className={cn(
                            'rounded-md border px-1.5 py-1.5 text-center text-[10px] font-medium transition-colors',
                            activeFilter === 'dueDate' && filterValue === opt.key
                              ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20'
                              : 'border-slate-300 bg-white text-gray-600 hover:border-slate-400 hover:bg-gray-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400 dark:hover:bg-white/8'
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2 rounded-lg border border-slate-300 bg-gray-50 p-2 dark:border-white/10 dark:bg-white/[0.035]">
                    <p className="px-1 pb-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300">Card status</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'complete', label: 'Marked complete' },
                        { value: 'incomplete', label: 'Not complete' },
                      ].map((status) => (
                        <button
                          key={status.value}
                          type="button"
                          onClick={() => {
                            setActiveFilter('status')
                            setFilterValue(status.value)
                            setFilterOpen(false)
                          }}
                          className={cn(
                            'rounded-md border px-2 py-1.5 text-center text-[10px] font-medium transition-colors',
                            activeFilter === 'status' && filterValue === status.value
                              ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20'
                              : 'border-slate-300 bg-white text-gray-600 hover:border-slate-400 hover:bg-gray-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400 dark:hover:bg-white/8',
                          )}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Label filter */}
                  {availableLabels.length > 0 && (
                    <div className="mt-2 rounded-lg border border-slate-300 bg-gray-50 p-2 dark:border-white/10 dark:bg-white/[0.035]">
                      <div className="flex items-center justify-between px-1 pb-1.5">
                        <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">Labels</p>
                        {canManageBoard && <span className="text-[10px] text-gray-400">Edit or remove</span>}
                      </div>
                      <div className="space-y-1">
                        {availableLabels.map((label) => (
                          editingLabelId === label._id ? (
                            <div key={label._id} className="flex items-center gap-1.5 rounded-md border border-primary/40 bg-white p-1 dark:bg-white/[0.06]">
                              <input
                                autoFocus
                                value={labelDraft}
                                onChange={(event) => setLabelDraft(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault()
                                    event.currentTarget.blur()
                                  }
                                  if (event.key === 'Escape') {
                                    event.preventDefault()
                                    skipLabelBlurRef.current = true
                                    setEditingLabelId(null)
                                  }
                                }}
                                onBlur={() => {
                                  if (skipLabelBlurRef.current) {
                                    skipLabelBlurRef.current = false
                                    return
                                  }
                                  saveLabel(label._id)
                                }}
                                className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-gray-800 outline-none focus:border-primary dark:border-white/15 dark:bg-[#202224] dark:text-gray-100"
                                aria-label={`Rename ${label.name}`}
                              />
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => saveLabel(label._id)}
                                className="rounded p-1 text-primary hover:bg-primary/10"
                                title="Save label"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div key={label._id} className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveFilter('label')
                                  setFilterValue(label._id)
                                  setFilterOpen(false)
                                }}
                                className={cn(
                                  'min-w-0 flex-1 truncate rounded border border-white/50 px-2 py-1 text-left text-[10px] font-semibold text-white transition-opacity',
                                  activeFilter === 'label' && filterValue === label._id ? 'ring-2 ring-primary/60' : 'opacity-80 hover:opacity-100'
                                )}
                                style={{ backgroundColor: label.color }}
                              >
                                {label.name}
                              </button>
                              {canManageBoard && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      skipLabelBlurRef.current = false
                                      setEditingLabelId(label._id)
                                      setLabelDraft(label.name)
                                    }}
                                    className="rounded p-1 text-gray-500 transition-colors hover:bg-slate-200 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                                    title={`Edit ${label.name}`}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setLabelPendingDelete({ id: label._id, name: label.name })
                                    }}
                                    className="rounded p-1 text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/15 dark:hover:text-red-300"
                                    title={`Delete ${label.name}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Assignee filter */}
                  {allAssignees.length > 0 && (
                    <div className="mt-2 rounded-lg border border-slate-300 bg-gray-50 p-2 dark:border-white/10 dark:bg-white/[0.035]">
                      <p className="px-1 pb-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300">Members</p>
                      <div className="max-h-32 space-y-0.5 overflow-y-auto">
                        {allAssignees.map((user) => (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => {
                              setActiveFilter('assignee')
                              setFilterValue(user._id)
                              setFilterOpen(false)
                            }}
                            className={cn(
                              'flex w-full items-center gap-2 rounded border px-2 py-1 text-left text-[11px] font-medium transition-colors',
                              activeFilter === 'assignee' && filterValue === user._id
                                ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20'
                                : 'border-slate-300 bg-white text-gray-600 hover:border-slate-400 hover:bg-gray-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400 dark:hover:bg-white/8'
                            )}
                          >
                            <div className={cn('flex h-4 w-4 items-center justify-center rounded-full text-[7px] font-bold text-white', getAvatarColorClass(user._id))}>
                              {(user.firstName?.[0] || '')}{(user.lastName?.[0] || '')}
                            </div>
                            {user.firstName} {user.lastName}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* ─── More Options Menu ─── */}
            <div className="relative" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setMoreMenuOpen((v) => !v)}
                className={cn('flex h-7 w-7 items-center justify-center rounded-md transition-all', useLightHeaderText ? 'text-white/90 hover:bg-white/15 hover:text-white' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/8')}
                title="More options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>

              {moreMenuOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-[#3a3b3d] dark:bg-[#282a2c]">
                  <button
                    type="button"
                    onClick={handleToggleVisibility}
                    disabled={!canManageBoard}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    {board.visibility === 'private' ? (
                      <><Globe className="h-3.5 w-3.5" /> Make Workspace visible</>
                    ) : (
                      <><Lock className="h-3.5 w-3.5" /> Make Private</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setArchiveView('cards')
                      setShowArchivedColumns(true)
                      setMoreMenuOpen(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    Archived items
                  </button>
                  <div className="my-1 border-t border-gray-100 dark:border-[#3a3b3d]" />
                  <button
                    type="button"
                    onClick={() => {
                      setShowBackgroundPicker(true)
                      setMoreMenuOpen(false)
                    }}
                    disabled={!canEditBoard}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <span className="h-3.5 w-3.5 rounded" style={{ background: board.background || '#004a6b' }} />
                    Change Background
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBoardTitleDraft(board.title)
                      setShowRenameBoard(true)
                      setMoreMenuOpen(false)
                    }}
                    disabled={!canEditBoard}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <AlignLeft className="h-3.5 w-3.5" />
                    Rename Board
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Board Columns Area ─── */}
      <div
        className="board-columns-area flex-1 overflow-x-auto overflow-y-hidden bg-[#fafbfc] px-5 py-5 dark:bg-[#1e1f21]"
        style={hasCustomBoardBackground ? { background: board.background } : undefined}
      >
        <div className="flex h-full gap-4">
          {lists?.map((list, listIndex) => (
            <div
              key={list._id}
              className={cn(
                'board-column flex h-fit max-h-full w-[248px] shrink-0 flex-col rounded-xl border border-gray-300 bg-[#f4f5f7] transition-all duration-150 dark:border-white/[0.06] dark:bg-[#282a2c]',
                dropTarget?.listId === list._id
                  ? 'ring-2 ring-primary/15'
                  : '',
                listDropTargetId === list._id && draggedListId !== list._id
                  ? 'ring-2 ring-primary/35'
                  : '',
              )}
              data-board-list-id={list._id}
              data-card-count={filteredCardsByList[list._id]?.length ?? 0}
              onDragOver={(event) => {
                if (!draggedListId || draggedListId === list._id) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setListDropTargetId(list._id)
              }}
              onDrop={(event) => {
                event.preventDefault()
                handleListDrop(list._id)
              }}
            >
              {/* Column header */}
              <div
                className="board-column-header flex shrink-0 cursor-grab items-center justify-between px-3.5 py-3 active:cursor-grabbing"
                draggable={canEditBoard}
                onDragStart={(event) => {
                  if (!canEditBoard) return
                  setDraggedListId(list._id)
                  event.dataTransfer.effectAllowed = 'move'
                  event.dataTransfer.setData('text/plain', list._id)
                }}
                onDragEnd={() => {
                  setDraggedListId(null)
                  setListDropTargetId(null)
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: list.color || getListStatusColor(listIndex) }}
                  />
                  {editingListId === list._id ? (
                    <input
                      autoFocus
                      value={listTitleDraft}
                      onChange={(event) => setListTitleDraft(event.target.value)}
                      onBlur={() => {
                        if (skipListBlurRef.current) {
                          skipListBlurRef.current = false
                          return
                        }
                        saveListTitle(list._id)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          event.currentTarget.blur()
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault()
                          skipListBlurRef.current = true
                          setEditingListId(null)
                        }
                      }}
                      onClick={(event) => event.stopPropagation()}
                      className="w-[118px] rounded border border-primary/40 bg-white px-1.5 py-0.5 text-[12px] font-semibold text-gray-700 outline-none dark:bg-[#333537] dark:text-gray-200"
                      aria-label={`Rename ${list.title}`}
                    />
                  ) : (
                    <h3
                      className="cursor-text truncate text-[12px] font-semibold text-gray-700 hover:text-primary dark:text-gray-200"
                      onClick={(event) => {
                        event.stopPropagation()
                        if (!canEditBoard) return
                        skipListBlurRef.current = false
                        setEditingListId(list._id)
                        setListTitleDraft(list.title)
                      }}
                      title="Click to rename list"
                    >
                      {list.title}
                    </h3>
                  )}
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-200/80 px-1 text-[9px] font-semibold text-gray-500 dark:bg-white/10 dark:text-gray-400">
                    {filteredCardsByList[list._id]?.length ?? 0}
                  </span>
                </div>
                <div className="flex items-center">
                  {canEditBoard && <button
                    type="button"
                    onClick={() => {
                      setAddingCardToList(list._id)
                      setNewCardTitle('')
                    }}
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
                    title="Add Card"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  }
                  {canEditBoard && <button
                    type="button"
                    onClick={() => setListPendingArchive({ id: list._id, title: list.title })}
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-gray-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    title="Archive Column"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                  }
                </div>
              </div>

              {/* Cards */}
              <div className="board-column-cards thin-scroll max-h-[calc(100vh-10rem)] space-y-2 overflow-y-auto px-2.5 pb-3">
                {filteredCardsByList[list._id]?.length === 0 &&
                  dropTarget?.listId === list._id && draggedCardId && (
                  <div
                    data-board-drop-placeholder
                    data-list-id={list._id}
                    data-card-index={0}
                    className="board-drop-placeholder mb-1.5 rounded-lg bg-gray-100/90 dark:bg-white/5"
                    style={{ height: `${dragPreviewHeight}px` }}
                  />
                )}
                {isFilterActive && (filteredCardsByList[list._id]?.length ?? 0) === 0 && (
                  <div className="px-1 pt-1.5 pb-1 text-[11px] text-gray-500 dark:text-gray-400">
                    0 cards match filters
                  </div>
                )}
                {filteredCardsByList[list._id]?.map((card, cardIndex) => (
                  <div key={card._id} className="relative">
                    {dropTarget?.listId === list._id && dropTarget?.index === cardIndex && draggedCardId && draggedCardId !== card._id && (
                      <div
                        data-board-drop-placeholder
                        data-list-id={list._id}
                        data-card-index={cardIndex}
                        className="board-drop-placeholder mb-1.5 rounded-lg bg-gray-100/90 dark:bg-white/5"
                        style={{ height: `${dragPreviewHeight}px` }}
                      />
                    )}
                    <div
                      data-board-card
                      data-list-id={list._id}
                      data-card-index={cardIndex}
                      onPointerDown={canEditBoard ? (e) => handleCardPointerDown(e, card._id) : undefined}
                      onPointerMove={canEditBoard ? handleCardPointerMove : undefined}
                      onPointerUp={canEditBoard ? handleCardPointerUp : undefined}
                      onPointerCancel={canEditBoard ? handleCardPointerCancel : undefined}
                      onClick={() => handleCardClick(card._id)}
                      className={cn(
                        'board-card group relative mb-0 cursor-pointer touch-none select-none rounded-xl border bg-white px-3 py-3 dark:bg-[#2c2d2f]',
                        canEditBoard ? 'cursor-grab active:cursor-grabbing' : '',
                        card.completedAt ? 'opacity-80' : '',
                        draggedCardId === card._id
                          ? 'border-dashed border-gray-300 bg-gray-100/40 opacity-40 shadow-none dark:border-gray-600 dark:bg-white/5'
                          : 'border-gray-300 shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:border-gray-400 hover:shadow-[0_4px_12px_rgba(15,23,42,0.10)] dark:border-white/[0.08] dark:hover:border-white/20 dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)]',
                      )}
                    >
                    {/* Labels */}
                    {card.labels && card.labels.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {card.labels.map((label) => (
                          <span
                            key={label._id}
                            className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                            style={{ backgroundColor: `${label.color}1A`, color: label.color }}
                            title={label.name}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Cover Image */}
                    {card.coverImage && (
                      <img
                        src={card.coverImage}
                        alt=""
                        draggable={false}
                        className="mb-2.5 h-28 w-full rounded-lg object-cover"
                      />
                    )}

                    {/* Priority badge */}
                    {card.priority !== 'none' && (
                      <div
                        className="mb-1.5 inline-flex items-center rounded-sm border px-1.5 py-0.5"
                        style={{
                          backgroundColor: `${PRIORITY_CONFIG[card.priority]?.accent}24`,
                          borderColor: `${PRIORITY_CONFIG[card.priority]?.accent}B3`,
                          color: PRIORITY_CONFIG[card.priority]?.accent,
                        }}
                      >
                        <span className="text-[9px] font-bold uppercase tracking-wide">
                          {PRIORITY_CONFIG[card.priority]?.label}
                        </span>
                      </div>
                    )}

                    {/* Title */}
                    <div className="relative">
                      {canEditBoard && (
                        <button
                          type="button"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => handleCardCompletion(event, card)}
                          title={card.completedAt ? 'Mark as incomplete' : 'Mark as complete'}
                          className={cn(
                            'group/complete absolute left-0 top-1/2 z-10 flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center rounded-full border-2 bg-transparent opacity-0 transition-all duration-200 group-hover:opacity-100',
                            card.completedAt
                              ? 'border-emerald-600 text-emerald-600 opacity-100 dark:border-emerald-400 dark:text-emerald-400'
                              : 'border-gray-400 text-transparent group-hover:border-gray-600 group-hover:ring-1 group-hover:ring-gray-300 hover:border-gray-600 dark:border-gray-500 dark:group-hover:border-white dark:group-hover:ring-white/25',
                          )}
                        >
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                          <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 whitespace-nowrap rounded bg-gray-800 px-1.5 py-1 text-[10px] font-semibold text-white opacity-0 shadow-sm transition-opacity duration-150 group-hover/complete:opacity-100 dark:bg-black">
                            {card.completedAt ? 'Mark incomplete' : 'Mark complete'}
                          </span>
                        </button>
                      )}
                      <p className={cn(
                        'text-[13px] font-semibold leading-[1.35] text-gray-800 transition-transform duration-300 ease-out dark:text-gray-100',
                        canEditBoard ? 'group-hover:translate-x-5' : '',
                        card.completedAt ? 'translate-x-5' : '',
                      )}>
                        {card.title}
                      </p>
                    </div>

                    {getCardRelationLabel(card.entityType) && (
                      <p className="mt-1 truncate text-[10px] font-medium text-gray-400 dark:text-gray-500" title={getCardRelationLabel(card.entityType) ?? undefined}>
                        {getCardRelationLabel(card.entityType)}
                      </p>
                    )}

                    {card.description?.trim() && (
                      <span title="This card has a description" className="mt-1 inline-flex items-center text-gray-400 dark:text-gray-500">
                        <AlignLeft className="h-3.5 w-3.5" />
                      </span>
                    )}

                    {/* Card metadata and assignees */}
                    <div className="mt-3 flex min-w-0 items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-[10px] font-medium text-gray-400 dark:text-gray-500">

                        {card.dueDate && (() => {
                          const due = new Date(card.dueDate)
                          const now = new Date()
                          const isOverdue = due < now
                          const isDueSoon = !isOverdue && (due.getTime() - now.getTime() <= 24 * 60 * 60 * 1000)
                          const isCompleted = !!card.completedAt
                          return (
                            <span
                              title={`Due date: ${due.toLocaleDateString()}`}
                              className={cn(
                                'inline-flex shrink-0 items-center gap-1 transition-colors',
                                isCompleted ? 'text-emerald-600 dark:text-emerald-400'
                                  : isOverdue ? 'text-red-600 dark:text-red-400'
                                  : isDueSoon ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-gray-400 dark:text-gray-500'
                              )}
                            >
                              <Clock className="h-3 w-3 shrink-0" />
                              <span>{due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            </span>
                          )
                        })()}

                        {Boolean(card.checklistTotal && card.checklistTotal > 0) && (() => {
                          const isAllDone = card.checklistCompleted === card.checklistTotal
                          return (
                            <span
                              title={`Checklist: ${card.checklistCompleted}/${card.checklistTotal} completed`}
                              className={cn(
                                'inline-flex shrink-0 items-center gap-1 transition-colors',
                                isAllDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'
                              )}
                            >
                              <CircleCheck className="h-3 w-3 shrink-0" />
                              <span>{card.checklistCompleted}/{card.checklistTotal}</span>
                            </span>
                          )
                        })()}

                        {Boolean(card.commentsCount && card.commentsCount > 0) && (
                          <span title={`${card.commentsCount} comments`} className="inline-flex shrink-0 items-center gap-1 text-gray-400 transition-colors dark:text-gray-500">
                            <MessageSquare className="h-3 w-3 shrink-0" />
                            <span>{card.commentsCount}</span>
                          </span>
                        )}

                        {Boolean(card.attachments && card.attachments.length > 0) && (
                          <span title={`${card.attachments?.length} attachment${card.attachments?.length === 1 ? '' : 's'}`} className="inline-flex shrink-0 items-center gap-1 text-gray-400 transition-colors dark:text-gray-500">
                            <Paperclip className="h-3 w-3 shrink-0" />
                            <span>{card.attachments?.length}</span>
                          </span>
                        )}
                      </div>

                      {card.assignees && card.assignees.length > 0 && (
                        <div className="ml-auto flex shrink-0 -space-x-1.5 pl-1" aria-label={`${card.assignees.length} assigned member${card.assignees.length === 1 ? '' : 's'}`}>
                          {card.assignees.slice(0, 3).map((u, index) => {
                            const isObj = typeof u === 'object' && u !== null
                            const firstName = isObj ? (u.firstName || '') : ''
                            const lastName = isObj ? (u.lastName || '') : ''
                            const initials = isObj ? `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() : '?'
                            const name = isObj ? `${firstName} ${lastName}`.trim() || 'User' : 'User'
                            const key = isObj ? u._id : String(u)
                            return (
                              <div key={key} className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[8px] font-bold text-white ring-2 ring-white shadow-sm dark:ring-[#2c2d2f]', getAvatarColorClassByIndex(index))} title={name}>
                                {initials}
                              </div>
                            )
                          })}
                          {card.assignees.length > 3 && (
                            <div title={`${card.assignees.length - 3} more member${card.assignees.length - 3 === 1 ? '' : 's'}`} className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[8px] font-bold text-gray-700 ring-2 ring-white dark:bg-gray-600 dark:text-gray-200 dark:ring-[#2c2d2f]">
                              +{card.assignees.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
                {filteredCardsByList[list._id]?.length > 0 &&
                  dropTarget?.listId === list._id &&
                  dropTarget?.index === filteredCardsByList[list._id].length &&
                  draggedCardId && (
                  <div
                    data-board-drop-placeholder
                    data-list-id={list._id}
                    data-card-index={filteredCardsByList[list._id].length}
                    className="board-drop-placeholder mb-1.5 rounded-lg bg-gray-100/90 dark:bg-white/5"
                    style={{ height: `${dragPreviewHeight}px` }}
                  />
                )}

                {/* Add card inline form */}
                {addingCardToList === list._id && (
                  <div data-add-card-form={list._id} className="rounded-xl border border-gray-300 bg-white p-2.5 shadow-sm dark:border-white/10 dark:bg-[#2c2d2f]">
                    <textarea
                      autoFocus
                      value={newCardTitle}
                      onChange={(e) => setNewCardTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddCard(list._id) }
                        if (e.key === 'Escape') setAddingCardToList(null)
                      }}
                      placeholder="Enter a title for this card…"
                      className="w-full resize-none rounded border-0 bg-transparent p-1 text-[12px] text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
                      rows={2}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button type="button" onClick={() => handleAddCard(list._id)} disabled={!newCardTitle.trim() || createCard.isPending} className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50">
                        Add card
                      </button>
                      <button type="button" onClick={() => setAddingCardToList(null)} className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {canEditBoard && addingCardToList !== list._id && (
                  <button
                    type="button"
                    onClick={() => { setAddingCardToList(list._id); setNewCardTitle('') }}
                    className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-medium text-gray-400 transition-colors hover:bg-white hover:text-gray-700 dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-300"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add card
                  </button>
                )}
              </div>

            </div>
          ))}

          {/* Add list column */}
          {canEditBoard && (
          <div className="w-[248px] shrink-0">
            {addingList ? (
              <div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-200/80 dark:bg-[#282a2c] dark:ring-[#3a3b3d]">
                <input
                  autoFocus
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddList()
                    if (e.key === 'Escape') setAddingList(false)
                  }}
                  placeholder="Enter list title…"
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-[#3a3b3d] dark:bg-[#2c2d2f] dark:text-gray-100 dark:placeholder:text-gray-500"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={handleAddList} disabled={!newListTitle.trim() || createList.isPending} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50">
                    Add list
                  </button>
                  <button type="button" onClick={() => setAddingList(false)} className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setAddingList(true); setNewListTitle('') }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-[13px] font-semibold text-gray-600 shadow-sm transition-all hover:border-primary/50 hover:bg-white hover:text-primary hover:shadow-md dark:border-white/15 dark:bg-[#282a2c] dark:text-gray-300 dark:hover:border-primary/50 dark:hover:bg-[#303234] dark:hover:text-primary"
              >
                <Plus className="h-4 w-4" />
                Add another list
              </button>
            )}
          </div>
          )}
        </div>
      </div>

      {/* ─── Trello-style Floating Drag Preview (100% full opacity, crisp, rotated) ─── */}
      {draggedCard && (
        <div
          ref={overlayRef}
          className="pointer-events-none fixed left-0 top-0 z-[9999] will-change-transform"
          style={{
            display: 'none',
            width: `${dragPreviewWidth}px`,
            transformOrigin: 'center center',
          }}
        >
          <div
            className="rounded-lg border-2 bg-white px-3 py-2.5 shadow-[0_20px_35px_rgba(0,0,0,0.28)] dark:bg-[#2c2d2f]"
            style={{
              borderColor: PRIORITY_CONFIG[draggedCard.priority]?.accent || '#94a3b8',
              borderLeftWidth: draggedCard.priority === 'none' ? '2px' : '4px',
            }}
          >
            {/* Labels */}
            {draggedCard.labels && draggedCard.labels.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {draggedCard.labels.map((label) => (
                  <span
                    key={label._id}
                    className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-semibold text-white"
                    style={{ backgroundColor: label.color }}
                  >
                    {label.name}
                  </span>
                ))}
              </div>
            )}

            {/* Cover Image */}
            {draggedCard.coverImage && (
              <img
                src={draggedCard.coverImage}
                alt=""
                className="mb-2 h-28 w-full rounded object-cover"
              />
            )}

            {/* Priority badge */}
            {draggedCard.priority !== 'none' && (
              <div
                className="mb-1 inline-flex items-center rounded-sm border px-1.5 py-0.5"
                style={{
                  backgroundColor: `${PRIORITY_CONFIG[draggedCard.priority]?.accent}24`,
                  borderColor: `${PRIORITY_CONFIG[draggedCard.priority]?.accent}B3`,
                  color: PRIORITY_CONFIG[draggedCard.priority]?.accent,
                }}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  {PRIORITY_CONFIG[draggedCard.priority]?.label}
                </span>
              </div>
            )}

            {/* Title */}
            <p className="text-[13px] font-medium text-gray-800 leading-snug dark:text-gray-100">
              {draggedCard.title}
            </p>
            {getCardRelationLabel(draggedCard.entityType) && (
              <p className="mt-1 truncate text-[10px] font-medium text-gray-400 dark:text-gray-500">
                {getCardRelationLabel(draggedCard.entityType)}
              </p>
            )}

            {/* Badges Row */}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-1.5">
              <div className="flex flex-wrap items-center gap-1.5 text-gray-400 dark:text-gray-500">
                {Boolean(draggedCard.description && draggedCard.description.trim().length > 0) && (
                  <span className="inline-flex items-center rounded p-0.5 text-gray-500 dark:text-gray-400">
                    <AlignLeft className="h-3 w-3" />
                  </span>
                )}

                {draggedCard.dueDate && (() => {
                  const due = new Date(draggedCard.dueDate)
                  const now = new Date()
                  const isOverdue = due < now
                  const isDueSoon = !isOverdue && (due.getTime() - now.getTime() <= 24 * 60 * 60 * 1000)
                  const isCompleted = !!draggedCard.completedAt
                  return (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold',
                        isCompleted ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          : isOverdue ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                          : isDueSoon ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                          : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      <Clock className="h-2.5 w-2.5 shrink-0" />
                      <span>{due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </span>
                  )
                })()}

                {Boolean(draggedCard.checklistTotal && draggedCard.checklistTotal > 0) && (
                  <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                  <CircleCheck className="h-2.5 w-2.5 shrink-0" />
                    <span>{draggedCard.checklistCompleted}/{draggedCard.checklistTotal}</span>
                  </span>
                )}

                {Boolean(draggedCard.commentsCount && draggedCard.commentsCount > 0) && (
                  <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                    <MessageSquare className="h-2.5 w-2.5 shrink-0" />
                    <span>{draggedCard.commentsCount}</span>
                  </span>
                )}
              </div>

              {draggedCard.assignees && draggedCard.assignees.length > 0 && (
                <div className="flex -space-x-1 ml-auto">
                  {draggedCard.assignees.slice(0, 3).map((u, index) => {
                    const isObj = typeof u === 'object' && u !== null
                    const firstName = isObj ? (u.firstName || '') : ''
                    const lastName = isObj ? (u.lastName || '') : ''
                    const initials = isObj ? `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() : '?'
                    const key = isObj ? u._id : String(u)
                    return (
                      <div key={key} className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[8px] font-bold text-white ring-1.5 ring-white dark:ring-[#2c2d2f]', getAvatarColorClassByIndex(index))}>
                        {initials}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Modal */}
      <CardDetailModal
        boardId={boardId ?? ''}
        cardId={selectedCardId}
        onClose={() => setSelectedCardId(null)}
      />

      <ConfirmDialog
        open={!!listPendingArchive}
        onClose={() => setListPendingArchive(null)}
        onConfirm={() => {
          if (!listPendingArchive || !boardId) return
          const listId = listPendingArchive.id
          setListPendingArchive(null)
          deleteList.mutate({ boardId, listId })
        }}
        title="Archive column?"
        message={`Cards in ${listPendingArchive?.title ?? 'this column'} will remain available on the board, but the column will be archived.`}
        confirmLabel="Archive column"
        variant="danger"
        confirmLoading={deleteList.isPending}
      />

      <ConfirmDialog
        open={!!memberPendingRemoval}
        onClose={() => setMemberPendingRemoval(null)}
        onConfirm={() => {
          if (!memberPendingRemoval || !boardId) return
          removeBoardMemberMutation.mutate(
            { boardId, member: memberPendingRemoval },
            { onSuccess: () => setMemberPendingRemoval(null) },
          )
        }}
        title="Remove board member?"
        message={`${memberPendingRemoval?.userId.firstName ?? 'This user'} will no longer appear in this board's member list.`}
        confirmLabel="Remove member"
        variant="danger"
        confirmLoading={removeBoardMemberMutation.isPending}
      />

      <ConfirmDialog
        open={!!labelPendingDelete}
        onClose={() => setLabelPendingDelete(null)}
        onConfirm={() => {
          if (!labelPendingDelete || !boardId) return
          deleteLabelMutation.mutate(
            { boardId, labelId: labelPendingDelete.id },
            {
              onSuccess: () => {
                if (filterValue === labelPendingDelete.id) handleClearFilter()
                setLabelPendingDelete(null)
              },
            },
          )
        }}
        title="Delete label?"
        message={`Removing ${labelPendingDelete?.name ?? 'this label'} will also remove it from cards that use it.`}
        confirmLabel="Delete label"
        variant="danger"
        confirmLoading={deleteLabelMutation.isPending}
      />

      <Modal
        open={showBackgroundPicker}
        onClose={() => setShowBackgroundPicker(false)}
        title="Colors"
        className="max-w-[440px]"
        footer={<Button type="button" variant="secondary" onClick={() => setShowBackgroundPicker(false)}>Done</Button>}
      >
        <div className="grid grid-cols-5 gap-2">
          {BOARD_BACKGROUND_PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => {
                updateBoardMutation.mutate({ id: boardId!, payload: { background: preset.value }, silent: true })
                setShowBackgroundPicker(false)
              }}
              className={cn(
                'aspect-square rounded-md border-2 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50',
                (!preset.value ? isDefaultBoardBackground : board.background === preset.value)
                  ? 'border-white ring-2 ring-primary shadow-md dark:border-white/80'
                  : !preset.value ? 'border-slate-300 dark:border-slate-500' : 'border-transparent',
              )}
              style={{ background: preset.value || '#ffffff' }}
              title={preset.name}
            >
              <span className="sr-only">{preset.name}</span>
            </button>
          ))}
        </div>

        <div className="my-4 border-t border-gray-200 dark:border-white/10" />
        <div className="grid grid-cols-5 gap-2">
          {BOARD_SOLID_BACKGROUNDS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => {
                updateBoardMutation.mutate({ id: boardId!, payload: { background: color === '#ffffff' ? '' : color }, silent: true })
                setShowBackgroundPicker(false)
              }}
              className={cn(
                'aspect-square rounded-md border-2 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50',
                (color === '#ffffff' ? isDefaultBoardBackground : board.background === color)
                  ? 'border-primary ring-2 ring-primary shadow-md'
                  : color === '#ffffff' ? 'border-gray-300 dark:border-gray-500' : 'border-transparent',
              )}
              style={{ backgroundColor: color }}
              aria-label={`Choose ${color} background`}
            />
          ))}
        </div>
      </Modal>

      <Modal
        open={showRenameBoard}
        onClose={() => setShowRenameBoard(false)}
        title="Rename board"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShowRenameBoard(false)}>Cancel</Button>
            <Button
              type="button"
              variant="primary"
              disabled={!boardTitleDraft.trim()} loading={updateBoardMutation.isPending}
              onClick={() => {
                if (!boardTitleDraft.trim() || !boardId) return
                updateBoardMutation.mutate({ id: boardId, payload: { title: boardTitleDraft.trim() }, silent: true })
                setShowRenameBoard(false)
              }}
            >
              Save name
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="board-title-draft">Board name</label>
        <input
          id="board-title-draft"
          autoFocus
          value={boardTitleDraft}
          onChange={(event) => setBoardTitleDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && boardTitleDraft.trim() && boardId) {
              updateBoardMutation.mutate({ id: boardId, payload: { title: boardTitleDraft.trim() }, silent: true })
              setShowRenameBoard(false)
            }
          }}
          className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          placeholder="Board name"
        />
      </Modal>

      <Modal
        open={showArchivedColumns}
        onClose={() => setShowArchivedColumns(false)}
        title="Archived items"
        className="max-w-xl"
        footer={<Button type="button" variant="secondary" onClick={() => setShowArchivedColumns(false)}>Close</Button>}
      >
        <div className="mb-5 rounded-xl border border-gray-100 bg-gray-50/80 p-3 dark:border-white/[0.08] dark:bg-white/[0.04]">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Restore archived work</p>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">Archived items stay safe until you restore them to the board.</p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1.5 dark:bg-white/[0.06]">
          {(['cards', 'columns'] as const).map((view) => (
            <button
              key={view}
              type="button"
              onClick={() => setArchiveView(view)}
              className={cn(
                'flex min-h-12 items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors',
                archiveView === view
                  ? 'bg-white text-gray-800 shadow-sm ring-1 ring-black/[0.03] dark:bg-[#3a3b3d] dark:text-gray-100 dark:ring-white/[0.06]'
                  : 'text-gray-500 hover:bg-white/60 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.04] dark:hover:text-gray-200',
              )}
            >
              <span className="flex items-center gap-2">
                {view === 'cards' ? <Archive className="h-4 w-4" /> : <Columns3 className="h-4 w-4" />}
                {view === 'cards' ? 'Cards' : 'Columns'}
              </span>
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[10px]',
                archiveView === view ? 'bg-primary/10 text-primary dark:bg-primary/20' : 'bg-gray-200/80 text-gray-500 dark:bg-white/10 dark:text-gray-400',
              )}>{view === 'cards' ? archivedCards?.length ?? 0 : archivedLists?.length ?? 0}</span>
            </button>
          ))}
        </div>

        {archiveView === 'cards' && (
          archivedCardsLoading ? (
            <SectionLoader variant="list" rows={3} label="Loading archived cards" />
          ) : archivedCards?.length ? (
            <div className="space-y-2.5">
              {archivedCards.map((card) => {
                const cardList = [...(lists ?? []), ...(archivedLists ?? [])].find((list) => list._id === card.listId)
                return (
                  <div key={card._id} className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-colors hover:border-gray-300 sm:flex-row sm:items-center dark:border-white/[0.09] dark:bg-white/[0.035] dark:hover:border-white/20">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-white/[0.08] dark:text-gray-400">
                      <Archive className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{card.title}</p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"><Columns3 className="h-3.5 w-3.5" />{cardList?.title ?? 'Archived column'}</p>
                    </div>
                    {canEditBoard && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => updateCardMutation.mutate({ boardId: boardId!, cardId: card._id, payload: { isArchived: false } })}
                        loading={updateCardMutation.isPending}
                        className="w-full shrink-0 justify-center text-xs sm:w-auto"
                      >
                        {updateCardMutation.isPending ? 'Restoring…' : <><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Restore</>}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-4 py-10 text-center dark:border-white/10 dark:bg-white/[0.025]">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-white/[0.07] dark:text-gray-500"><Archive className="h-5 w-5" /></div>
              <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-gray-300">No archived cards</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Cards you archive will appear here.</p>
            </div>
          )
        )}

        {archiveView === 'columns' && (
          archivedListsLoading ? (
            <SectionLoader variant="list" rows={3} label="Loading archived columns" />
          ) : archivedLists?.length ? (
            <div className="space-y-2.5">
              {archivedLists.map((list) => {
                const archivedCardCount = cards?.filter((card) => card.listId === list._id).length ?? 0
                return (
                  <div key={list._id} className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-colors hover:border-gray-300 sm:flex-row sm:items-center dark:border-white/[0.09] dark:bg-white/[0.035] dark:hover:border-white/20">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-white/[0.08]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: list.color || '#7b68ee' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{list.title}</p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {archivedCardCount} {archivedCardCount === 1 ? 'card' : 'cards'} in this column
                      </p>
                    </div>
                    {canEditBoard && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => updateList.mutate({ boardId: boardId!, listId: list._id, payload: { isArchived: false } })}
                        loading={updateList.isPending}
                        className="w-full shrink-0 justify-center text-xs sm:w-auto"
                      >
                        {updateList.isPending ? 'Restoring…' : <><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Restore</>}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-4 py-10 text-center dark:border-white/10 dark:bg-white/[0.025]">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-white/[0.07] dark:text-gray-500"><Columns3 className="h-5 w-5" /></div>
              <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-gray-300">No archived columns</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Columns you archive will appear here.</p>
            </div>
          )
        )}
      </Modal>
    </div>
  )
}

BoardViewPage.displayName = 'BoardViewPage'
